import { useState } from "react";
import { FEATURES } from "@/mocks/contents";
import type { FeatureItem } from "@/mocks/contents";

const EXTRA: string[] = [
  "复盘的价值不在定论，而在把每一环的关键时间点、关键说法与后续更正排列成线。读者可以据此快速判断哪些信息已有官方回应、哪些仍停留在“据称”阶段，真正实现看瓜不迷路。",
  "面对一张没有上下文的截图，最好的习惯是先问三件事：它最早出现在哪、当事人有没有回应、转发它的账号是否可信。我们把这套方法固化成了编辑审稿的默认动作，也建议你收藏备用。",
  "吃瓜形态的演化，本质是公众对“信息可追溯”的渴望不断升级。从逐帧解读到多信源交叉，真正有价值的内容不是情绪，而是把事情讲清楚的能力。这也是我们坚持的方向。",
];

function FeatureCard({ item, index }: { item: FeatureItem; index: number }) {
  const [readMore, setReadMore] = useState(false);
  const [follow, setFollow] = useState(false);

  return (
    <article className="flex flex-col gap-0 overflow-hidden rounded-2xl border border-background-200 bg-background-50 lg:flex-row">
      <div className="relative h-56 w-full shrink-0 overflow-hidden sm:h-72 lg:h-auto lg:w-[42%]">
        <img
          src={item.img}
          alt={item.title}
          className="h-full w-full object-cover object-center"
        />
        <span className="absolute left-4 top-4 rounded-full bg-background-50/90 px-3 py-1 text-xs font-semibold text-primary-700">
          {item.kicker}
        </span>
      </div>
      <div className="flex flex-1 flex-col justify-center p-6 md:p-8">
        <h3 className="font-heading text-2xl font-black leading-snug text-foreground-950 md:text-[28px]">
          {item.title}
        </h3>
        <p className="mt-4 text-sm leading-relaxed text-foreground-600">{item.summary}</p>
        {readMore && (
          <p className="mt-3 border-l-2 border-accent-400 pl-3 text-sm leading-relaxed text-foreground-600">
            {EXTRA[index]}
          </p>
        )}
        <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-500">
          <span className="inline-flex items-center gap-1">
            <i className="ri-eye-line text-sm" aria-hidden="true"></i>
            {item.reads}
          </span>
          <span className="inline-flex items-center gap-1">
            <i className="ri-time-line text-sm" aria-hidden="true"></i>
            {item.minutes}
          </span>
        </div>
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => setReadMore((v) => !v)}
            className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            {readMore ? "收起摘要" : "继续阅读"}
            <i className={`text-base ${readMore ? "ri-arrow-up-line" : "ri-arrow-right-line"}`} aria-hidden="true"></i>
          </button>
          <button
            type="button"
            onClick={() => setFollow((v) => !v)}
            aria-pressed={follow}
            className={`inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-full px-5 py-2.5 text-sm font-semibold ring-1 transition-colors ${
              follow
                ? "bg-accent-500 text-background-50 ring-accent-500"
                : "bg-background-50 text-foreground-700 ring-background-300 hover:text-accent-700"
            }`}
          >
            <i className={`text-base ${follow ? "ri-notification-4-fill" : "ri-notification-3-line"}`} aria-hidden="true"></i>
            {follow ? "已加入追更" : "追更本篇"}
          </button>
        </div>
      </div>
    </article>
  );
}

export default function FeatureEditorial() {
  return (
    <section id="feature" className="w-full bg-background-50 py-12 md:py-16" aria-label="深度盘点">
      <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
        <div className="max-w-2xl">
          <span className="text-xs font-bold tracking-[0.2em] text-accent-600">FEATURE · 深度盘点</span>
          <h2 className="mt-2 font-heading text-3xl font-black text-foreground-950 md:text-4xl">
            把瓜吃明白，而不只是看热闹
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-foreground-600">
            每篇深度盘点都包含完整时间线、多方说法与公开素材梳理。读它，比刷十条零散帖子更接近事件全貌。
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="flex flex-col gap-6 lg:col-span-2">
            {FEATURES.map((item, i) => (
              <FeatureCard key={i} item={item} index={i} />
            ))}
          </div>

          <aside className="flex flex-col gap-5" aria-label="深度盘点侧栏">
            <div className="rounded-2xl border border-background-200 bg-background-100 p-6">
              <div className="flex items-center gap-2">
                <i className="ri-questionnaire-line text-xl text-primary-600" aria-hidden="true"></i>
                <h3 className="font-heading text-lg font-bold text-foreground-950">为何坚持“时间线”写法？</h3>
              </div>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                舆论最容易被断章取义带偏。时间线能让人一眼看到事件从发生、发酵到回应的完整顺序，避免情绪先行、真相后置。
              </p>
            </div>

            <div className="rounded-2xl border border-background-200 bg-background-100 p-6">
              <h3 className="font-heading text-lg font-bold text-foreground-950">三分钟核瓜法</h3>
              <ul className="mt-4 space-y-3">
                {["先找信源首发，再读平台标记", "对比多方回应，别只信一方叙事", "留意“据称”与“证实”的措辞差异"].map((t, i) => (
                  <li key={t} className="flex items-start gap-3">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-accent-500 font-heading text-xs font-bold text-background-50">
                      {i + 1}
                    </span>
                    <span className="text-sm leading-snug text-foreground-700">{t}</span>
                  </li>
                ))}
              </ul>
              <a
                href="#faq"
                className="mt-5 inline-flex cursor-pointer items-center gap-1 text-sm font-semibold text-primary-700 hover:text-primary-600"
              >
                查看更多内容原则
                <i className="ri-arrow-right-line text-sm" aria-hidden="true"></i>
              </a>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}